import React, { useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    TextField,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { EMPTY_TRANSACTION } from '../constants';
import { Transaction } from '../types';
import dayjs, { Dayjs } from 'dayjs'
import {
    DataProviderInputMappingColumnsModel,
    getDataProviderInputMappingColumns
} from "../services/dataProviderInputMappingService.ts";
import {lowerFirstLetter} from "../utils/nameUtils.ts";

const TransactionDetail = () => {
    const { transactionId } = useParams(); // Get transactionId from URL params
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [transaction, setTransaction] = useState({ ...EMPTY_TRANSACTION });
    const [pbms, setPbms] = useState<Array<{ id: number; pbmName: string }>>([]);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [dataProviderMappings, setDataProviderMappings] = useState<DataProviderInputMappingColumnsModel[]>([]);

    useEffect(() => {
        // Fetch PBM data
        apiClient.get<Array<{ id: number; pbmName: string }>>('/api/PBM')
            .then(data => setPbms(data))
            .catch(err => console.error("[TransactionDetail] Error fetching data from /api/PBM", err));

        // Fetch the transaction details by ID
        // Define the API response type with string dates
        type TransactionResponse = {
            [K in keyof Transaction]: K extends keyof Transaction
                ? Transaction[K] extends Dayjs | null | undefined
                    ? string | null | undefined
                    : Transaction[K]
                : never;
        };

        apiClient.get<TransactionResponse>(`/api/transaction/${transactionId}`)
            .then(data => {
                // Need to parse date strings into date objects.
                // Probably should make a middle ware function for this

                // Convert string dates to Dayjs objects
                const processedData: Transaction = {
                    ...data,
                    dateFilled: data.dateFilled ? dayjs(data.dateFilled) : null,
                    dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth) : null,
                    dateWritten: data.dateWritten ? dayjs(data.dateWritten) : null,
                    dateEnteredByPharmacy: data.dateEnteredByPharmacy ? dayjs(data.dateEnteredByPharmacy) : null,
                    dateReceivedByNASH: data.dateReceivedByNASH ? dayjs(data.dateReceivedByNASH) : null,
                    priorAuthApprovalDate: data.priorAuthApprovalDate ? dayjs(data.priorAuthApprovalDate) : null,
                    priorAuthExpirationDate: data.priorAuthExpirationDate ? dayjs(data.priorAuthExpirationDate) : null,
                    processDateTime: data.processDateTime ? dayjs(data.processDateTime) : null
                };
                setTransaction(processedData);
            })
            .catch(err => console.error("[TransactionDetail] Error fetching transaction", err));
    }, [transactionId]);

    useEffect(() => {
        const fetchDataProviderMappings = async () => {
            try{
                const mappingsData = await getDataProviderInputMappingColumns('Nash Default');
                setDataProviderMappings(mappingsData);
            } catch (error) {
                console.log('Error fetching data provider mappings', error);
            } 
        }

        fetchDataProviderMappings();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});
        
        try {
            await apiClient.patch(`/api/transaction/${transactionId}`, transaction);
            // Preserve search parameters when returning to transaction list
            const currentParams = new URLSearchParams(searchParams);
            navigate(`/transactions?${currentParams.toString()}`);
        } catch (error) {
            if (error instanceof Error && error.message) {
                try {
                    const errorData = JSON.parse(error.message);
                    if (errorData.Field && errorData.Message) {
                        setFieldErrors({ [errorData.Field]: errorData.Message });
                    }
                } catch {
                    console.error('[Error Updating Transaction]', error);
                }
            }
        }
    };

    const handleDelete = async () => {
        if (window.confirm("Are you sure you want to delete this transaction?")) {
            await apiClient.delete(`/api/transaction/${transactionId}`)
                .catch(e => console.error('[Error Deleting Transaction]', e));
            // Preserve search parameters when returning after delete
            const currentParams = new URLSearchParams(searchParams);
            navigate(`/transactions?${currentParams.toString()}`);
        }
    };

    const handleDateChange = (field: keyof Transaction, value: any) => {
        setTransaction(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const getLabel = (field: keyof Transaction, defaultLabel: string) => {
        const mapping = dataProviderMappings.find(x => lowerFirstLetter(x.destinationColumn) == field);
        return mapping?.inputColumn ?? defaultLabel;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
                <Card>
                    <CardHeader title="Edit Transaction" />
                    <CardContent>
                        
                            <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel>PBM</InputLabel>
                                    <Select
                                        value={transaction.pbmId || ''}
                                        label="PBM"
                                        onChange={(e) => setTransaction({ ...transaction, pbmId: Number(e.target.value) })}
                                        required
                                    >
                                        {pbms && Array.isArray(pbms) ? pbms.map(pbm => (
                                            <MenuItem key={pbm.id} value={pbm.id}>{pbm.pbmName}</MenuItem>
                                        )): <></>}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={getLabel('pharmNabp', 'Pharm NABP')}
                                    value={transaction.pharmNabp || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, pharmNabp: e.target.value });
                                        if (fieldErrors.pharmNabp) {
                                            setFieldErrors(prev => {
                                                const { pharmNabp, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    error={!!fieldErrors.pharmNabp}
                                    helperText={fieldErrors.pharmNabp}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('rxNum', 'Rx Number')}
                                    value={transaction.rxNum || ""}
                                    onChange={(e) => setTransaction({ ...transaction, rxNum: e.target.value })}
                                    error={!!fieldErrors.rxNum}
                                    helperText={fieldErrors.rxNum}
                                />

                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateFilled', 'Date Filled')}
                                        value={transaction.dateFilled}
                                        onChange={(value) => handleDateChange('dateFilled', value)}
                                    />
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={getLabel('groupId', 'Group ID')}
                                    value={transaction.groupId || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, groupId: e.target.value });
                                        if (fieldErrors.groupId) {
                                            setFieldErrors(prev => {
                                                const { groupId, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    error={!!fieldErrors.groupId}
                                    helperText={fieldErrors.groupId}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('cardHolderId', 'Card Holder ID')}
                                    value={transaction.cardHolderId || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, cardHolderId: e.target.value });
                                        if (fieldErrors.cardHolderId) {
                                            setFieldErrors(prev => {
                                                const { cardHolderId, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    inputProps={{ maxLength: 45 }}
                                    error={!!fieldErrors.cardHolderId}
                                    helperText={fieldErrors.cardHolderId}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('otherId', 'Other ID')}
                                  value={transaction.otherId || ""}
                                  onChange={(e) => {
                                      setTransaction({ ...transaction, otherId: e.target.value });
                                      if (fieldErrors.otherId) {
                                          setFieldErrors(prev => {
                                              const { otherId, ...rest } = prev;
                                              return rest;
                                          });
                                      }
                                  }}
                                  inputProps={{ maxLength: 100 }}
                                  error={!!fieldErrors.otherId}
                                  helperText={fieldErrors.otherId}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('nashPatientId', 'NASH Patient ID')}
                                  value={transaction.nashPatientId || ""}
                                  onChange={(e) => {
                                      setTransaction({ ...transaction, nashPatientId: e.target.value });
                                      if (fieldErrors.nashPatientId) {
                                          setFieldErrors(prev => {
                                              const { nashPatientId, ...rest } = prev;
                                              return rest;
                                          });
                                      }
                                  }}
                                  inputProps={{ maxLength: 100 }}
                                  error={!!fieldErrors.nashPatientId}
                                  helperText={fieldErrors.nashPatientId}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('submittedPc', 'Submitted PC')}
                                    value={transaction.submittedPc || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, submittedPc: e.target.value });
                                        if (fieldErrors.submittedPc) {
                                            setFieldErrors(prev => {
                                                const { submittedPc, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    inputProps={{ maxLength: 3 }}
                                    error={!!fieldErrors.submittedPc}
                                    helperText={fieldErrors.submittedPc}
                                />

                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateOfBirth', 'Date of Birth')}
                                        value={transaction.dateOfBirth}
                                        onChange={(value) => handleDateChange('dateOfBirth', value)}
                                        slotProps={{
                                            textField: { required: true }
                                        }}
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={transaction.genderCode || ""}
                                        label={getLabel('genderCode', 'Gender')}
                                        onChange={(e) => setTransaction({ ...transaction, genderCode: e.target.value })}
                                        
                                    >
                                        <MenuItem value="Not Specified">Not Specified</MenuItem>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                        <MenuItem value="Non Binary">Non Binary</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={getLabel('patientFirstName', 'Patient First Name')}
                                    value={transaction.patientFirstName || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, patientFirstName: e.target.value });
                                        if (fieldErrors.patientFirstName) {
                                            setFieldErrors(prev => {
                                                const { patientFirstName, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    required
                                    inputProps={{ maxLength: 12 }}
                                    error={!!fieldErrors.patientFirstName}
                                    helperText={fieldErrors.patientFirstName}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('patientMiddleName', 'Patient Middle Name')}
                                  value={transaction.patientMiddleName || ""}
                                  onChange={(e) => {
                                      setTransaction({ ...transaction, patientMiddleName: e.target.value });
                                      if (fieldErrors.patientMiddleName) {
                                          setFieldErrors(prev => {
                                              const { patientMiddleName, ...rest } = prev;
                                              return rest;
                                          });
                                      }
                                  }}
                                  inputProps={{ maxLength: 100 }}
                                  error={!!fieldErrors.patientMiddleName}
                                  helperText={fieldErrors.patientMiddleName}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('patientLastName', 'Patient Last Name')}
                                    value={transaction.patientLastName || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, patientLastName: e.target.value });
                                        if (fieldErrors.patientLastName) {
                                            setFieldErrors(prev => {
                                                const { patientLastName, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    required
                                    inputProps={{ maxLength: 15 }}
                                    error={!!fieldErrors.patientLastName}
                                    helperText={fieldErrors.patientLastName}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('refillCode', 'Refill Code')}
                                    type="number"
                                    value={transaction.refillCode || 0}
                                    onChange={(e) => setTransaction({ ...transaction, refillCode: parseInt(e.target.value) })}
                                    
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('daysSupply', 'Days Supply')}
                                    type="number"
                                    value={transaction.daysSupply || 0}
                                    onChange={(e) => setTransaction({ ...transaction, daysSupply: parseInt(e.target.value) })}
                                    
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('ndc', 'NDC')}
                                    value={transaction.ndc || ""}
                                    onChange={(e) => setTransaction({ ...transaction, ndc: e.target.value })}
                                    
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('ingrCostSubmitted', 'Ingredient Cost Submitted')}
                                    type="number"
                                    value={transaction.ingrCostSubmitted || 0}
                                    onChange={(e) => setTransaction({ ...transaction, ingrCostSubmitted: parseFloat(e.target.value) })}
                                    
                                    inputProps={{ step: "0.01" }}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('prescriberId', 'Prescriber ID')}
                                    value={transaction.prescriberId || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, prescriberId: e.target.value });
                                        if (fieldErrors.prescriberId) {
                                            setFieldErrors(prev => {
                                                const { prescriberId, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    inputProps={{ maxLength: 10 }}
                                    error={!!fieldErrors.prescriberId}
                                    helperText={fieldErrors.prescriberId}
                                />

                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateWritten', 'Date Written')}
                                        value={transaction.dateWritten}
                                        onChange={(value) => handleDateChange('dateWritten', value)}
                                    />
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={getLabel('numRefillsAuthorized', 'Number of Refills Authorized')}
                                    type="number"
                                    value={transaction.numRefillsAuthorized || ""}
                                    onChange={(e) => setTransaction({ ...transaction, numRefillsAuthorized: parseInt(e.target.value) })}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('unc', 'UNC')}
                                    type="number"
                                    value={transaction.unc || ""}
                                    onChange={(e) => setTransaction({ ...transaction, unc: parseFloat(e.target.value) })}
                                    inputProps={{ step: "0.01" }}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('qtyDispensed', 'Quantity Dispensed')}
                                    type="number"
                                    value={transaction.qtyDispensed || 0}
                                    onChange={(e) => setTransaction({ ...transaction, qtyDispensed: parseFloat(e.target.value) })}
                                    
                                    inputProps={{ step: "0.01" }}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('claimMessage', 'Claim Message')}
                                    value={transaction.claimMessage || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, claimMessage: e.target.value });
                                        if (fieldErrors.claimMessage) {
                                            setFieldErrors(prev => {
                                                const { claimMessage, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    
                                    inputProps={{ maxLength: 200 }}
                                    error={!!fieldErrors.claimMessage}
                                    helperText={fieldErrors.claimMessage}
                                />

                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateEnteredByPharmacy', 'Entered Date')}
                                        value={transaction.dateEnteredByPharmacy}
                                        onChange={(value) => handleDateChange('dateEnteredByPharmacy', value)}
                                    />
                                </FormControl>

                                <TextField
                                  fullWidth
                                  label={getLabel('statusCode', 'Status Code')}
                                  value={transaction.statusCode || ""}
                                  onChange={(e) => setTransaction({ ...transaction, statusCode: e.target.value })}
                                  error={!!fieldErrors.statusCode}
                                  helperText={fieldErrors.statusCode}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('serviceProviderId', 'Service Provider ID')}
                                  type="number"
                                  value={transaction.serviceProviderId || ""}
                                  onChange={(e) => setTransaction({ ...transaction, serviceProviderId: parseInt(e.target.value) })}
                                  inputProps={{ step: "1" }}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('copay', 'Copay')}
                                  type="number"
                                  value={transaction.copay || 0}
                                  onChange={(e) => setTransaction({ ...transaction, copay: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('patientPayAmount', 'Patient Pay Amount')}
                                  type="number"
                                  value={transaction.patientPayAmount || 0}
                                  onChange={(e) => setTransaction({ ...transaction, patientPayAmount: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyTotalPaid', 'Pharmacy Total Paid')}
                                  type="number"
                                  value={transaction.pharmacyTotalPaid || 0}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyTotalPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyIngredientCostPaid', 'Pharmacy Ingredient Cost Paid')}
                                  type="number"
                                  value={transaction.pharmacyIngredientCostPaid || 0}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyIngredientCostPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyDispensingFeePaid', 'Pharmacy Dispensing Fee Paid')}
                                  type="number"
                                  value={transaction.pharmacyDispensingFeePaid || 0}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyDispensingFeePaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyTaxPaid', 'Pharmacy Tax Paid')}
                                  type="number"
                                  value={transaction.pharmacyTaxPaid || 0}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyTaxPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellIngredientCost', 'Sell Ingredient Cost')}
                                  type="number"
                                  value={transaction.sellIngredientCost || 0}
                                  onChange={(e) => setTransaction({ ...transaction, sellIngredientCost: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellDispensingFee', 'Sell Dispensing Fee')}
                                  type="number"
                                  value={transaction.sellDispensingFee || 0}
                                  onChange={(e) => setTransaction({ ...transaction, sellDispensingFee: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellTax', 'Sell Tax')}
                                  type="number"
                                  value={transaction.sellTax || 0}
                                  onChange={(e) => setTransaction({ ...transaction, sellTax: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('totalClientBilled', 'Total Client Billed')}
                                  type="number"
                                  value={transaction.totalClientBilled || 0}
                                  onChange={(e) => setTransaction({ ...transaction, totalClientBilled: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('planPay', 'Plan Pay')}
                                  type="number"
                                  value={transaction.planPay || 0}
                                  onChange={(e) => setTransaction({ ...transaction, planPay: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  
                                />

                                <FormControl fullWidth>
                                    <DateTimePicker
                                      label={getLabel('processDateTime', 'Process Date / Time')}
                                      value={transaction.processDateTime}
                                      views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                                      onChange={(value) => handleDateChange('processDateTime', value)}
                                    />
                                </FormControl>

                                <Stack direction="row" spacing={2}>
                                    <Button type="submit" variant="contained" fullWidth>
                                        Save Changes
                                    </Button>

                                    <Button type="button" variant="contained" fullWidth onClick={handleDelete} color="error">
                                        Delete Transaction
                                    </Button>
                                </Stack>
                            </Stack>
                            </form>
                        
                    </CardContent>
                </Card>
            </Box>
        </LocalizationProvider>
    );
};

export default TransactionDetail;
